import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MdDeleteOutline } from 'react-icons/md';
import styles from './styles.module.css';
import { Button, Dropdown, Input, SearchableSelect } from '../../../../../common/ui';
import { searchProductsForAddStock } from '../../../stocks/actions';
import { createStockTransfer, getStocksByProductForTransfer } from '../../actions';

function getProductSearchOptionLabel(option) {
  if (!option || typeof option !== 'object') return '—';
  const mainName =
    option.baseName != null && String(option.baseName).trim() !== ''
      ? option.baseName
      : option.name;
  const parts = [option.code, mainName, option.variantName, option.barcode].filter(
    (x) => x != null && String(x).trim() !== ''
  );
  return parts.join(' – ') || '—';
}

function formatStockOptionLabel(stock) {
  const created = stock.created_at
    ? new Date(stock.created_at).toLocaleDateString()
    : '-';
  const price =
    stock.selling_price != null ? Number(stock.selling_price).toFixed(2) : '-';
  const balance =
    stock.qty != null ? Number(stock.qty).toFixed(5) : '0.00000';
  return `${created} | ${price} | ${balance}`;
}

const TransferStockForm = ({
  auth: { branchList = [], branchId, companyId },
  stocksState: { suggestProducts, isSuggestedProductsLoading },
  searchProductsForAddStock,
}) => {
  const navigate = useNavigate();

  const branchOptions = useMemo(
    () =>
      (branchList || []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [branchList]
  );

  const defaultBranch = branchId || branchOptions?.[0]?.value || '';
  const [branchFrom, setBranchFrom] = useState(defaultBranch);
  const [branchTo, setBranchTo] = useState(defaultBranch);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [productStocks, setProductStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const searchDebounceRef = useRef(null);

  const [newRow, setNewRow] = useState({
    productId: null,
    fromStockId: null,
    quantity: '',
  });

  const productSearchOptions = Array.isArray(suggestProducts) ? suggestProducts : [];
  const mergedProductOptions = useMemo(() => {
    const map = new Map();
    if (selectedProduct?.id != null) {
      map.set(String(selectedProduct.id), selectedProduct);
    }
    productSearchOptions.forEach((item) => {
      if (item?.id != null) {
        map.set(String(item.id), item);
      }
    });
    return Array.from(map.values());
  }, [productSearchOptions, selectedProduct]);

  const stockDropdownOptions = useMemo(
    () =>
      productStocks.map((stock) => ({
        value: stock.id,
        label: formatStockOptionLabel(stock),
        stock,
      })),
    [productStocks]
  );

  const selectedStock = useMemo(
    () => productStocks.find((s) => String(s.id) === String(newRow.fromStockId)),
    [productStocks, newRow.fromStockId]
  );

  const availableQty = selectedStock ? Number(selectedStock.qty) || 0 : 0;

  useEffect(() => {
    if (!newRow.productId || !branchFrom) {
      setProductStocks([]);
      return undefined;
    }

    let cancelled = false;
    setStocksLoading(true);

    getStocksByProductForTransfer(branchFrom, newRow.productId, companyId)
      .then((data) => {
        if (!cancelled) setProductStocks(data);
      })
      .catch(() => {
        if (!cancelled) setProductStocks([]);
      })
      .finally(() => {
        if (!cancelled) setStocksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [newRow.productId, branchFrom, companyId]);

  const resetAddForm = () => {
    setNewRow({
      productId: null,
      fromStockId: null,
      quantity: '',
    });
    setSelectedProduct(null);
    setProductStocks([]);
  };

  const handleOpenAddPanel = () => {
    resetAddForm();
    setIsAddPanelOpen(true);
  };

  const handleProductChange = (value) => {
    const product = mergedProductOptions.find((p) => String(p.id) === String(value)) || null;
    if (product) {
      setSelectedProduct(product);
    }
    setNewRow({
      productId: value,
      fromStockId: null,
      quantity: '',
    });
  };

  const handleProductSearch = (keyword) => {
    const term = keyword != null ? String(keyword).trim() : '';
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (term.length < 1) return;

    searchDebounceRef.current = setTimeout(() => {
      searchProductsForAddStock(term);
    }, 250);
  };

  useEffect(
    () => () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    },
    []
  );

  const onAddRow = () => {
    if (!newRow.productId || !newRow.fromStockId || !newRow.quantity) return;

    const quantity = Number(newRow.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    if (quantity > availableQty) return;

    const unitCost = Number(selectedStock?.unit_cost) || 0;
    const unitPrice = Number(selectedStock?.selling_price) || 0;
    const effectiveProduct =
      selectedProduct ||
      mergedProductOptions.find((p) => String(p.id) === String(newRow.productId)) ||
      null;

    setRows((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length + 1}`,
        code: effectiveProduct?.code || '-',
        product: getProductSearchOptionLabel(effectiveProduct),
        quantity,
        unit: selectedStock?.unit || 'pcs',
        unitCost,
        costOfGoods: unitCost * quantity,
        unitPrice,
        salesValue: unitPrice * quantity,
        fromStockId: newRow.fromStockId,
        productId: newRow.productId,
      },
    ]);

    resetAddForm();
    setIsAddPanelOpen(false);
  };

  const handleRemoveRow = (rowId) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!branchFrom) {
      alert('Please select branch from.');
      return;
    }
    if (!branchTo) {
      alert('Please select branch to.');
      return;
    }
    if (String(branchFrom) === String(branchTo)) {
      alert('Branch from and branch to must be different.');
      return;
    }
    if (rows.length === 0) {
      alert('Please add at least one item.');
      return;
    }

    const items = rows.map((row) => ({
      productId: row.productId,
      fromStockId: row.fromStockId,
      qty: Number(row.quantity),
    }));

    setIsSubmitting(true);
    try {
      await createStockTransfer({
        companyId,
        fromBranchId: branchFrom,
        toBranchId: branchTo,
        items,
      });
      navigate('/inventory/transfer-stocks');
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create stock transfer.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageTop}>
        <h1 className={styles.pageTitle}>Transfer Stocks</h1>
        <p className={styles.breadcrumb}>Inventory / Transfered Stocks / Transfer Stocks</p>
      </div>

      <div className={styles.transferForm}>
        <div className={styles.branchRow}>
          <Dropdown
            id="branch-from"
            label="Branch From"
            value={branchFrom}
            options={branchOptions}
            onChange={setBranchFrom}
          />
          <Dropdown
            id="branch-to"
            label="Branch To"
            value={branchTo}
            options={branchOptions}
            onChange={setBranchTo}
          />
        </div>

        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div>
              <h2 className={styles.tableTitle}>Transfer Products</h2>
              <p className={styles.tableSubtitle}>View your transfer products here</p>
            </div>
            <Button onClick={handleOpenAddPanel}>Add Item</Button>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>CODE</th>
                  <th>PRODUCT</th>
                  <th>QUANTITY</th>
                  <th>UNIT</th>
                  <th>UNIT COST</th>
                  <th>COST OF GOODS</th>
                  <th>UNIT PRICE</th>
                  <th>SALES VALUE</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className={styles.emptyRow}>
                      No items added
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.code}</td>
                      <td>{row.product}</td>
                      <td>{row.quantity.toFixed(4)}</td>
                      <td>{row.unit}</td>
                      <td>{row.unitCost.toFixed(2)}</td>
                      <td>{row.costOfGoods.toFixed(2)}</td>
                      <td>{row.unitPrice.toFixed(2)}</td>
                      <td>{row.salesValue.toFixed(2)}</td>
                      <td>
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => handleRemoveRow(row.id)}
                          aria-label="Remove item"
                          title="Remove item"
                        >
                          <MdDeleteOutline />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.footerActions}>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/inventory/transfer-stocks')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>

      {isAddPanelOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsAddPanelOpen(false)} />
          <aside className={styles.addPanel}>
            <h2 className={styles.addPanelTitle}>Add Product</h2>

            <SearchableSelect
              label="Product"
              id="product-search"
              options={mergedProductOptions}
              value={newRow.productId}
              onChange={handleProductChange}
              onSelect={(option) => setSelectedProduct(option)}
              getOptionLabel={getProductSearchOptionLabel}
              getOptionValue={(option) => option.id}
              placeholder="Search by code, name or barcode"
              async
              onSearch={handleProductSearch}
              isLoading={isSuggestedProductsLoading}
              minSearchLength={1}
            />

            <Dropdown
              id="stock-select"
              label="Stock"
              value={newRow.fromStockId}
              options={stockDropdownOptions}
              onChange={(value) =>
                setNewRow((prev) => ({ ...prev, fromStockId: value, quantity: '' }))
              }
              placeholder={
                !newRow.productId
                  ? 'Select a product first'
                  : stocksLoading
                    ? 'Loading stocks...'
                    : stockDropdownOptions.length === 0
                      ? 'No stock available'
                      : 'Created Date | Price | Balance Quantity'
              }
              disabled={
                !newRow.productId || stocksLoading || stockDropdownOptions.length === 0
              }
            />

            <Input
              label="Quantity"
              id="transfer-qty"
              type="number"
              value={newRow.quantity}
              onChange={(value) => setNewRow((prev) => ({ ...prev, quantity: value }))}
              placeholder="Enter transfer quantity"
              disabled={!newRow.fromStockId}
            />
            {selectedStock && (
              <p className={styles.availableHint}>
                Available balance: {availableQty.toFixed(5)} {selectedStock.unit || ''}
              </p>
            )}

            <div className={styles.addPanelFooter}>
              <Button
                onClick={onAddRow}
                disabled={
                  !newRow.productId ||
                  !newRow.fromStockId ||
                  !newRow.quantity ||
                  Number(newRow.quantity) <= 0 ||
                  Number(newRow.quantity) > availableQty
                }
              >
                Add
              </Button>
              <Button variant="secondary" onClick={() => setIsAddPanelOpen(false)}>
                Cancel
              </Button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

TransferStockForm.propTypes = {
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  stocksState: PropTypes.shape({
    suggestProducts: PropTypes.array,
    isSuggestedProductsLoading: PropTypes.bool,
  }),
  searchProductsForAddStock: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  stocksState: state.stocksState,
});

export default connect(mapStateToProps, {
  searchProductsForAddStock,
})(TransferStockForm);
